import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {
  formatMetricNumber,
  INFLUENCER_METRICS,
  METRIC_CHART_COLORS,
  type SentimentInfluencerWithPosts,
  type InfluencerMetricKey,
} from "@/lib/influencers-api";

interface InfluencerMetricsChartProps {
  influencer: SentimentInfluencerWithPosts;
  maxValues: Record<string, number>;
  className?: string;
}

const METRIC_LABELS: Record<InfluencerMetricKey, () => string> = {
  combined_influence_score: () => m.metric_score(),
  followers: () => m.metric_followers(),
  total_engagements: () => m.metric_engagement(),
  total_likes: () => m.metric_likes(),
  total_shares: () => m.metric_shares(),
  total_comments: () => m.metric_comments(),
};

export function InfluencerMetricsChart({
  influencer,
  maxValues,
  className,
}: InfluencerMetricsChartProps) {
  return (
    <div className={cn("space-y-2.5", className)}>
      {INFLUENCER_METRICS.map((metric, index) => {
        const value = influencer[metric.key] as number;
        const maxValue = maxValues[metric.key] || 1;
        const percentage = Math.max(5, (value / maxValue) * 100);
        const color = METRIC_CHART_COLORS[index % METRIC_CHART_COLORS.length];

        return (
          <div key={metric.key} className="group">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-muted-foreground/70 text-[11px] font-medium tracking-wide uppercase">
                {METRIC_LABELS[metric.key]()}
              </span>
              <span className="text-foreground/90 text-[11px] font-semibold tabular-nums">
                {formatMetricNumber(value)}
              </span>
            </div>
            <div className="bg-muted/30 relative h-3.5 overflow-hidden rounded-full">
              <div
                className={cn(
                  "absolute inset-y-0 left-0 rounded-full bg-gradient-to-r transition-all duration-500 ease-out",
                  color
                )}
                style={{width: `${percentage}%`}}
              />
              {/* Subtle glow effect on hover */}
              <div
                className={cn(
                  "pointer-events-none absolute inset-y-0 left-0 rounded-full bg-gradient-to-r opacity-0 blur-sm transition-opacity group-hover:opacity-50",
                  color
                )}
                style={{width: `${percentage}%`}}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
